import { useCallback, useEffect, useMemo, useState } from 'react'
import kitApi, { KitJobAdDto } from '@/api/kit/KitApi'
import { formatDateAllInfo } from '@/utils/dateUtils'
import Button from '@/components/Button'
import KitPaginationControls from './KitPaginationControls'

const PAGE_SIZE = 50
const JOB_ADS_TABLE_CLASS =
    'main-table w-full min-w-[900px] text-[11px] leading-tight'
const JOB_ADS_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const JOB_ADS_CELL_CLASS = 'px-2 py-1 align-top'

export default function KitJobAdsTab(): React.JSX.Element {
    const [jobAds, setJobAds] = useState<KitJobAdDto[]>([])
    const [pageNumber, setPageNumber] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasRequested, setHasRequested] = useState(false)

    const fetchJobAds = useCallback(async (page: number) => {
        try {
            setIsLoading(true)
            setError(null)
            const res = await kitApi.getJobAds({
                pageNumber: page,
                perPage: PAGE_SIZE,
            })
            setJobAds(res.jobAds)
            setHasMore(res.hasMore)
        } catch (err) {
            console.error('Failed to load Kit job ads', err)
            setError('Unable to load Kit job ads.')
        } finally {
            setIsLoading(false)
            setHasRequested(true)
        }
    }, [])

    useEffect(() => {
        void fetchJobAds(pageNumber)
    }, [fetchJobAds, pageNumber])

    const tableRows = useMemo(
        () =>
            jobAds.map((ad) => (
                <tr key={ad.id}>
                    <td className={JOB_ADS_CELL_CLASS}>{ad.id}</td>
                    <td className={JOB_ADS_CELL_CLASS}>{ad.name}</td>
                    <td className={JOB_ADS_CELL_CLASS}>{ad.workEmail}</td>
                    <td className={JOB_ADS_CELL_CLASS}>
                        {ad.linkedinUrl ? (
                            <a
                                className="text-blue-600 hover:underline text-xs font-semibold break-all"
                                href={ad.linkedinUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {ad.linkedinUrl}
                            </a>
                        ) : (
                            <span className="text-gray-500 text-xs">—</span>
                        )}
                    </td>
                    <td className={JOB_ADS_CELL_CLASS}>{ad.jobTitle}</td>
                    <td className={JOB_ADS_CELL_CLASS}>
                        {formatDateAllInfo(ad.createdAt)}
                    </td>
                    <td className={JOB_ADS_CELL_CLASS}>{ad.createdBy}</td>
                </tr>
            )),
        [jobAds],
    )

    return (
        <div>
            <div className="flex items-center justify-between">
                <h3 className="page-title">Job Ads</h3>
                <Button
                    type="button"
                    style="secondary"
                    onClick={() => void fetchJobAds(pageNumber)}
                    enabled={!isLoading}
                >
                    Refresh
                </Button>
            </div>
            {error && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            {isLoading && !hasRequested ? (
                <p className="mt-4 text-sm text-gray-500">Loading job ads...</p>
            ) : jobAds.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                    {hasRequested
                        ? 'No job ads found.'
                        : 'Select this tab to load job ads.'}
                </p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className={JOB_ADS_TABLE_CLASS}>
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide">
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        Job Ad Id
                                    </th>
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        Name
                                    </th>
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        Work Email
                                    </th>
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        LinkedIn URL
                                    </th>
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        Job Title
                                    </th>
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        Created At
                                    </th>
                                    <th className={JOB_ADS_HEADER_CELL_CLASS}>
                                        Created By
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{tableRows}</tbody>
                        </table>
                    </div>
                    <KitPaginationControls
                        pageNumber={pageNumber}
                        itemCount={jobAds.length}
                        perPage={PAGE_SIZE}
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onPrevious={() =>
                            setPageNumber((page) => Math.max(1, page - 1))
                        }
                        onNext={() => setPageNumber((page) => page + 1)}
                    />
                </>
            )}
        </div>
    )
}
